/**
 * DePdfViewerEmbed — the national label-PDF viewer for BfArM (de_pdf), AIFA
 * (it_pdf) and HPRA (ie_pdf).  German / Italian: the shared
 * BilingualPdfViewerEmbed (page boxes + hover English panel), Latin script, so
 * a query is boxed on the page AND marked in the English panel — same settings
 * as the Swiss viewer.  Irish labels are English: the plain regulatory viewer.
 */
import { forwardRef } from 'react';
import { dePdfApi, iePdfApi, itPdfApi } from '../../api/createViewerApi';
import { authApi } from '../../services/authApi';
import BilingualPdfViewerEmbed, {
  type BilingualPdfViewerEmbedProps,
  type BilingualPdfViewerHandle,
  type BilingualViewerConfig,
} from './BilingualPdfViewerEmbed';
import RegulatoryViewerEmbed from './RegulatoryViewerEmbed';

export type LabelPdfFamily = 'de_pdf' | 'it_pdf' | 'ie_pdf';

export const DE_CONFIG: BilingualViewerConfig = {
  viewerApi: dePdfApi,
  translate: (text, preserveHtml, docId, sectionId) =>
    authApi.translateDePdfText(text, preserveHtml, docId, sectionId),
  sourceLabel: 'Original',
  sourceName: 'original-language',
  titleSrcField: 'title_src',
  // German can't be told from English by script → search both.
  docSearch: 'both',
  translateTerm: (en, docId) => authApi.translateTermToSource('de_pdf', en, docId),
};

export const IT_CONFIG: BilingualViewerConfig = {
  ...DE_CONFIG,
  viewerApi: itPdfApi,
  translateTerm: (en, docId) => authApi.translateTermToSource('it_pdf', en, docId),
  translate: (text, preserveHtml, docId, sectionId) =>
    authApi.translateItPdfText(text, preserveHtml, docId, sectionId),
};

type Props = BilingualPdfViewerEmbedProps & { family?: LabelPdfFamily };

const DePdfViewerEmbed = forwardRef<BilingualPdfViewerHandle, Props>(
  function DePdfViewerEmbed({ family = 'de_pdf', ...props }, ref) {
    if (family === 'ie_pdf') {
      // ref kept: keyword Compare jumps to the match through it.
      return <RegulatoryViewerEmbed ref={ref as never} viewerApi={iePdfApi} {...props} />;
    }
    return <BilingualPdfViewerEmbed ref={ref} config={family === 'it_pdf' ? IT_CONFIG : DE_CONFIG} {...props} />;
  },
);

export default DePdfViewerEmbed;
